import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import * as Yup from 'yup';
import {Column, Row} from '../../../../../../../components/layout';
import ArrowIcon from '../../../../../../medical-network/ArrowIcon';
import './styles.scss';
import * as fonts from '../../../../../../../util/fonts';
import {strings} from '../../../../../../../util/strings';
import {
  AppForm,
  AppFormDatePickerField,
  AppFormSelectField,
} from '../../../../../../../components/forms';
import moment from 'moment';
import {useMediaQuery} from 'react-responsive';

interface DayTimeFilterProps {
  buStyles: any;
  labels: typeof strings.pt.appointments;
  initialValue?: Date;
  goBack(value: any): void;
  showDoctorSearch: boolean;
  selectedDoctor: any;
  doctorAvailableSlots: any[];
  doctorAvailableSlotsFromToday: any[];
  getSlotsByTime(
    startDate: any,
    startTime: any,
    endDate: any,
    endTime: any,
    doctor?: any,
  ): void;
  getAvailableSlotsByTime(
    startDate: any,
    startTime: any,
    endDate: any,
    endTime: any,
    doctor?: any[],
  ): void;
  isLoading: boolean;
  firstDateAvailable?: any;
}

const validationSchema = Yup.object().shape({
  date: Yup.date().required(),
});

function DayTimeFilter({
  buStyles,
  labels,
  initialValue,
  goBack,
  showDoctorSearch,
  doctorAvailableSlots,
  doctorAvailableSlotsFromToday,
  getSlotsByTime,
  getAvailableSlotsByTime,
  selectedDoctor,
  isLoading,
  firstDateAvailable,
}: DayTimeFilterProps) {
  const appointmentsLabels = {
    ...strings.pt.appointments,
    ...labels,
  };
  const formatFixedTimedPeriods = (label: string) => {
    const tokens = label?.split(',');
    let result = tokens.map((tkn: any) => {
      return {label: tkn, value: tkn};
    });
    return result;
  };

  const timeperiods = formatFixedTimedPeriods(
    appointmentsLabels.schedule_appointment_time_periods,
  );
  const [fixedTimePeriod, setFixedTimePeriod] = useState(timeperiods);
  const [formikContext, setFormikContext] = useState<any>();
  const [selectedFixedPeriod, setSelectedFixedPeriod] = useState(null);
  const [timeIntervals, setTimeIntervals] = useState(fixedTimePeriod);
  const [selectedInterval, setSelectedInterval] = useState({});
  const [selectedDate, setSelectedDate]: any = useState(
    initialValue ? initialValue : new Date(),
  );

  const isResponsive = useMediaQuery({maxDeviceWidth: 768});

  const getSlotsDefault = () => {
    const dateToday = moment(selectedDate, 'YYYY-MM-DD').isSame(
      moment(new Date()),
      'day',
    );

    if (dateToday) {
      const currentHour = new Date().getHours();
      const filteredTimePeriods = fixedTimePeriod.filter((timePeriod: any) => {
        const endTime = parseInt(
          timePeriod.value?.split('-')[1]?.split(':')[0],
        ); // Get the end hour of the time period
        return currentHour < endTime; // Return true if current hour is before the end hour
      });
      setFixedTimePeriod(filteredTimePeriods);
      !selectedFixedPeriod &&
        handleIntervalChange(filteredTimePeriods[0].value, null, [
          selectedDoctor.id,
        ]);
    } else {
      setFixedTimePeriod(timeperiods);
      !selectedFixedPeriod &&
        handleIntervalChange(fixedTimePeriod[0].value, null, [
          selectedDoctor.id,
        ]);
    }
  };

  const generateIntervals = (date: any) => {
    const dateToday = moment(date, 'H:mm').isSame(moment(new Date()), 'day');
    if (dateToday) {
      const currentHour = new Date().getHours();
      const filteredTimePeriods = fixedTimePeriod.filter((timePeriod: any) => {
        const endTime = parseInt(
          timePeriod.value?.split('-')[1]?.split(':')[0],
        ); // Get the end hour of the time period
        return currentHour < endTime; // Return true if current hour is before the end hour
      });
      setTimeIntervals(filteredTimePeriods);
      setSelectedInterval(filteredTimePeriods[0].value);
      handleIntervalChange(filteredTimePeriods[0].value, date);
    } else {
      setTimeIntervals(fixedTimePeriod);
      handleIntervalChange(selectedInterval, date);
    }
  };

  useEffect(() => {
    if (!selectedDoctor) {
      generateIntervals(moment(initialValue ? initialValue : new Date()));
    } else {
      getSlotsDefault();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDoctor, initialValue]);

  const handleDateChange = (value: any) => {
    if (value !== undefined) {
      setSelectedDate(moment(value, 'HH:mm'));
      selectedDoctor
        ? handleIntervalChange(selectedFixedPeriod, moment(value), [
            selectedDoctor.id,
          ])
        : generateIntervals(value);
    }
  };

  const handleIntervalChange = (value: any, date?: any, doctorId?: any[]) => {
    if (!(Object.keys(value).length === 0 && value.constructor === Object)) {
      doctorId ? setSelectedFixedPeriod(value) : setSelectedInterval(value);
      getSlotsByTime(
        value?.split('-')[0],
        moment(date || selectedDate).format('YYYY/MM/DD'),
        value?.split('-')[1],
        moment(date || selectedDate).format('YYYY/MM/DD'),
        doctorId,
      );
    }
  };

  return (
    <div
      className="filter-container"
      style={{marginTop: showDoctorSearch ? '30px' : 'inherit'}}>
      {!isResponsive && !showDoctorSearch && (
        <div className="daytime-filter-title-container">
          <div className="return-label-container">
            <div
              className="return-to-previous-page"
              onClick={() => goBack(true)}>
              <ArrowIcon classNames="arrow-goback" />
              <a
                className="app-header-bu-logo-opacity"
                style={{
                  ...fonts.SM,
                  ...fonts.SemiBold,
                  color: buStyles.primaryColor,
                }}>
                {labels?.appointments_goback}
              </a>
            </div>
          </div>
          <span style={{...fonts.MM}} className="see-daytime-label">
            {labels?.appointments_see_daytime}
          </span>
        </div>
      )}
      {isResponsive && !showDoctorSearch && (
        <Row className="daytime-filter-title-container-responsive">
          <div
            className="return-to-previous-page-responsive"
            onClick={() => goBack(true)}>
            <ArrowIcon classNames="arrow-goback" />
            <a
              className="app-header-bu-logo-opacity"
              style={{
                ...fonts.SM,
                ...fonts.SemiBold,
                color: buStyles.primaryColor,
              }}>
              {labels?.appointments_goback}
            </a>
          </div>
        </Row>
      )}
      <AppForm
        initialValues={{
          date: new Date(),
          period: '',
          interval: '',
        }}
        className="app-form-filter-fields"
        validationSchema={validationSchema}
        onSubmit={(values) => {}}>
        {!isResponsive && (
          <Row className="filter-fields-row">
            {!selectedDoctor && (
              <>
                <Column className="date-column-start-end">
                  <span style={{...fonts.SM}} className="search-field-title">
                    {appointmentsLabels.appointments_date}
                  </span>
                  <AppFormDatePickerField
                    className="date-input"
                    selectedValue={initialValue ? initialValue : undefined}
                    name="date"
                    minDate={new Date()}
                    dontShowYears={true}
                    onChange={(value) =>
                      handleDateChange(value)
                    }></AppFormDatePickerField>
                </Column>
                <Column className="date-column-start-end">
                  <span style={{...fonts.SM}} className="search-field-title">
                    {appointmentsLabels.appointments_interval}
                  </span>
                  <AppFormSelectField
                    showErrorMessage
                    className="interval-input"
                    name="interval"
                    options={timeIntervals}
                    value={selectedInterval}
                    onChange={(value) => handleIntervalChange(value)}
                    handleFormikContext={(formik) => {
                      if (
                        !!!formikContext ||
                        formik.errors !== (formikContext as any).errors
                      ) {
                        setFormikContext(formik);
                      }
                    }}></AppFormSelectField>
                </Column>
              </>
            )}
            {selectedDoctor && (
              <>
                <Column className="date-column-start-end">
                  <span style={{...fonts.SM}} className="search-field-title">
                    {appointmentsLabels.appointments_date}
                  </span>
                  <AppFormDatePickerField
                    className="date-input"
                    selectedValue={initialValue ? initialValue : undefined}
                    name="date"
                    value={selectedDate}
                    minDate={new Date()}
                    custom={true}
                    isLoading={isLoading}
                    firstDateAvailable={firstDateAvailable}
                    getAvailableSlotsByTime={(
                      sD: any,
                      sT: any,
                      eD: any,
                      eT: any,
                    ) => getAvailableSlotsByTime(sD, sT, eD, eT)}
                    doctorAvailableSlots={doctorAvailableSlots}
                    doctorAvailableSlotsFromToday={
                      doctorAvailableSlotsFromToday
                    }
                    onChange={(value) =>
                      handleDateChange(value)
                    }></AppFormDatePickerField>
                </Column>
                <Column className="date-column">
                  <span style={{...fonts.SM}} className="search-field-title">
                    {appointmentsLabels.appointments_interval}
                  </span>
                  <AppFormSelectField
                    showErrorMessage
                    className="period-input"
                    name="period"
                    options={fixedTimePeriod}
                    value={selectedFixedPeriod}
                    onChange={(value) => {
                      handleIntervalChange(value, selectedDate, [
                        selectedDoctor.id,
                      ]);
                    }}
                    handleFormikContext={(formik) => {
                      if (
                        !!!formikContext ||
                        formik.errors !== (formikContext as any).errors
                      ) {
                        setFormikContext(formik);
                      }
                    }}></AppFormSelectField>
                </Column>
              </>
            )}
          </Row>
        )}
        {isResponsive && (
          <Column className="filter-fields-column">
            <Column className="date-column-start-end-responsive">
              <Row style={{...fonts.SM}} className="search-field-title">
                {labels?.appointments_date}
              </Row>
              <AppFormDatePickerField
                className="date-input"
                selectedValue={initialValue ? initialValue : undefined}
                name="date"
                minDate={new Date()}
                dontShowYears={true}
                onChange={(value) =>
                  handleDateChange(value)
                }></AppFormDatePickerField>
            </Column>
            <Column className="date-column-start-end-responsive">
              <Row style={{...fonts.SM}} className="search-field-title">
                {labels?.appointments_interval}
              </Row>
              <AppFormSelectField
                showErrorMessage
                className="interval-input"
                name="period"
                options={fixedTimePeriod}
                value={selectedFixedPeriod}
                onChange={(value) => handleIntervalChange(value)}
                handleFormikContext={(formik) => {
                  if (
                    !!!formikContext ||
                    formik.errors !== (formikContext as any).errors
                  ) {
                    setFormikContext(formik);
                  }
                }}></AppFormSelectField>
            </Column>
          </Column>
        )}
      </AppForm>
    </div>
  );
}

const mapStateToProps = ({strings, globalStyles}: any) => ({
  labels: strings.value.pt.appointments,
  buStyles: globalStyles.value,
});

export default compose(connect(mapStateToProps)(DayTimeFilter));
